import axios, { AxiosError, type AxiosInstance } from 'axios';
import { config } from './env';
import { ApiError, type ProblemDetails } from '@/types/api.types';

/** Header carrying the reCAPTCHA v3 token. The server always verifies it independently. */
export const RECAPTCHA_TOKEN_HEADER = 'X-Recaptcha-Token';

/** What any ordinary call may take before the browser gives up on it. */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Claims is allowed longer because its response grows with the policy: a large one takes the BF API
 * upwards of twenty seconds to assemble, and portal-backend gives that domain a budget to match. A
 * client ceiling below the server's budget aborts a request the backend is still honestly working on,
 * and the provider is told claims are unavailable when nothing failed — so this stays above it.
 */
export const CLAIMS_TIMEOUT_MS = 90_000;

// Normalises every failure into ApiError, so components render from a single error shape and never leak
// raw axios internals into the UI.
function toApiError(error: unknown): ApiError {
  if (error instanceof AxiosError) {
    const problem = error.response?.data as ProblemDetails | undefined;
    const message = problem?.detail ?? problem?.title ?? 'The request could not be completed.';
    return new ApiError(message, error.response?.status, problem);
  }

  return new ApiError('The request could not be completed.', undefined, undefined);
}

/**
 * The single HTTP client for the portal. Every call goes through the TanStack Query layer — no component
 * or hook outside a feature's hooks folder may call this directly.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    throw toApiError(error);
  },
);
